import { useAuth } from "@/core/auth/hooks/useAuth";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { EmptyState } from "@/shared/components/ui/EmptyState";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { RowSkeleton } from "@/shared/components/ui/RowSkeleton";
import { calendarDate, calendarDateKey } from "@/shared/utils/calendarDate";
import { useAvailabilities, useAvailabilityOverrides } from "../hooks/useAvailabilities";

const weekdays = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"] as const;
const noTimesMessage = "No available times for this day. Choose another date.";

export function DateAvailabilityEmptyState({ dateKey, locationId }: { dateKey: string; locationId: string }) {
  const { user } = useAuth();
  const { businessId } = useCurrentBusiness();
  const scope = { userId: user?.id ?? "", businessId: businessId ?? "", locationId };
  const availability = useAvailabilities(scope);
  const overrides = useAvailabilityOverrides({ ...scope, startDate: dateKey, endDate: dateKey });

  if (availability.isFetching || overrides.isFetching) return <RowSkeleton />;
  const error = availability.error ?? overrides.error;
  if (error) {
    return (
      <ErrorState
        title="Couldn't check working hours"
        message={error.message}
        onRetry={() => { void availability.refetch(); void overrides.refetch(); }}
      />
    );
  }

  const inScope = (value: typeof scope & { deletedAt: Date | null }) =>
    value.userId === scope.userId && value.businessId === scope.businessId &&
    value.locationId === scope.locationId && !value.deletedAt;
  const schedules = (availability.data ?? []).filter(inScope);
  const schedule = schedules.find(value => value.isActive);
  let message = noTimesMessage;

  if (!schedule) {
    message = schedules.length
      ? "Your working hours are inactive for this location."
      : "You haven't set up working hours for this location.";
  } else {
    // Match booking precedence: time off wins, then the most recent date-specific hours.
    const override = (overrides.data ?? [])
      .filter(value => inScope(value) && value.isActive &&
        calendarDateKey(value.effectiveFrom) <= dateKey && calendarDateKey(value.effectiveTo) >= dateKey)
      .sort((a, b) => Number(b.overrideType === "unavailable") - Number(a.overrideType === "unavailable") ||
        b.effectiveFrom.getTime() - a.effectiveFrom.getTime() ||
        b.updatedAt.getTime() - a.updatedAt.getTime() ||
        b.createdAt.getTime() - a.createdAt.getTime() || a.id.localeCompare(b.id))[0];
    if (override?.overrideType === "unavailable") {
      message = override.reason?.trim()
        ? `You've marked this date as unavailable. Reason: ${override.reason.trim()}`
        : "You've marked this date as unavailable. Choose another date.";
    } else if (!override) {
      const day = weekdays[calendarDate(dateKey).getDay()];
      if (schedule.schedule[day].length === 0) {
        const label = day.charAt(0).toUpperCase() + day.slice(1);
        message = `You don't work on ${label}s at this location. Choose another date or update your working hours.`;
      }
    }
  }

  return <EmptyState message={message} />;
}
